import React from "react";
import { Box, FormControl, FormControlLabel, InputLabel, makeStyles, Radio, RadioGroup } from "@material-ui/core";
import { Theme } from "../.themes";
import HelpIcon, { HelpProps } from "./HelpIcon";
import clsx from "clsx";

export type RadioProps = {
  value: any;
  title: string;
};

export type RadioButtonGroupBaseProps = {
  label?: {
    id?: string;
    title?: string;
  };
  help?: HelpProps;
  onChange?: Function;
  id?: string;
  values?: RadioProps[];
  selected: any;
  showError?: boolean;
  buttonStyle?: string;
};

const useStyles = makeStyles<Theme, RadioButtonGroupBaseProps>((theme: Theme) => ({
  labelWrapper: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
  },
  help: {
    textAlign: "right",
    paddingLeft: "8px",
  },
  gridContainer: props => ({
    display: "flex",
    flexDirection: "row",
    width: "auto",
    rowGap: "16px",
    columnGap: "12px",
    justifyContent: props.values?.length === 1 ? "center" : theme.brand === "polestar" ? "none" : "space-between",
    margin: "0px",
    padding: "0px",
    "& .MuiFormControlLabel-root": {
      marginLeft: "0px",
      marginRight: "0px",
    },
  }),
  button: {
    width: "200px",
    boxSizing: "border-box",
    backgroundColor: theme.radioButton?.backgroundColor ?? "#FFFFFF",
    border: theme.radioButton?.border ?? `1px solid ${theme.palette.divider}`,
    borderRadius: theme.radioButton?.borderRadius ?? theme.shape?.borderRadius,
    [theme.breakpoints.down("xs")]: {
      flexBasis: "calc(50% - 6px)",
    },
    [theme.breakpoints.down(400)]: {
      flexBasis: "100%",
    },
    "&:hover": {
      backgroundColor: theme.radioButton?.hoverBackgroundColor ?? "#FFFFFF",
    },
  },
  activeButton: {
    backgroundColor: theme.radioButton?.activeBackgroundColor ?? "#FFFFFF",
    border: theme.radioButton?.activeBorder ?? `1px solid ${theme.palette.primary.main}`,
    "& .MuiFormControlLabel-label": {
      fontWeight: theme.radioButton?.activeFontWeight ?? 500,
    },
  },
}));

export const RadioButtonGroupBase: React.FC<RadioButtonGroupBaseProps> = (props: RadioButtonGroupBaseProps) => {
  const classes = useStyles(props);
  const label = props.label;
  const help = props.help;

  const handleChange = (event: any) => {
    if (props.onChange) {
      props.onChange(event.target.value);
    }
  };

  let labelElement;
  if (label && label?.title) {
    labelElement = (
      <InputLabel id={label?.id} shrink={false}>
        {label?.title}
      </InputLabel>
    );
  }

  let helpElement;
  if (help && (help?.title || help?.description)) {
    helpElement = <HelpIcon title={help?.title} description={help?.description}></HelpIcon>;
  }

  let labelArea;
  if (labelElement) {
    labelArea = (
      <Box className={classes.labelWrapper}>
        {labelElement}
        <Box className={classes.help}>{helpElement}</Box>
      </Box>
    );
  }

  return (
    <Box marginY={1} id={"ws-radioButton-" + props?.id}>
      {labelArea}
      <FormControl fullWidth>
        {props.values && props.values.length > 0 && (
          <RadioGroup
            value={props.selected}
            name='RadioButtonsGroup'
            onChange={event => handleChange(event)}
            className={classes.gridContainer}
          >
            {props.values.map((x, index) => (
              <FormControlLabel
                className={clsx(classes.button, props.selected === x.value && classes.activeButton, props.buttonStyle)}
                key={index}
                value={x.value}
                control={<Radio />}
                label={x.title}
              />
            ))}
          </RadioGroup>
        )}
      </FormControl>
    </Box>
  );
};
